import React, { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Callout, useOverlayContext } from 'seagull';
import { useStoreActions } from "../../contexts";
import { handleRequest } from "../../infra/utils";
import ManualBillModal from "../ManualBillModal/ManualBillModal";
import { getChargeSuccessToastText } from "./AddChargeModal.utils";
import styles from "./AddChargeModal.module.scss";
import { utils } from "../../utils";
import { getCreditsWarning } from "../../business-logic/billingLogic";
import { toDatestampObject } from "../../models/time";
import { useBillingObservable } from "../../providers/BillingObservableProvider";
import BillChargeTableItem from "../BillClientModal/BillChargeTableItem/BillChargeTableItem";
import { useBillingModeData } from "../BillClientModal/BillingDate/useBillingModeData";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { BillMode } from "../BillClientModal/BillClientModal.types";
import { discountToDTO, getTotal } from "../BillClientModal/BillClientModal.utils";
import { FIRST_ITEM_INDEX } from "../BillClientModal/constants";
import { createEmptyChargeItem } from "../BillClientModal/BillClientItem.utils";
import { BillChargeClientApprovalCallout } from "../BillClientModal/Callouts/Callouts";
const addChargeLoader = 'addCharge';
export default function AddChargeModal(props) {
  const {
    context,
    dismissModal
  } = props;
  const {
    relationship,
    invoiceDateInfo,
    earliestBillableDate,
    totalCredits
  } = context;
  const {
    notifyToRefresh
  } = useBillingObservable();
  const {
    logBI,
    api
  } = useStoreActions();
  const {
    startLoader,
    stopLoader,
    showSuccessToast,
    showErrorToast
  } = useOverlayContext();
  const {
    BILL_CLIENT
  } = useFeatureFlags();
  const {
    billModeOptions,
    defaultMode,
    defaultIssueDate
  } = useBillingModeData({
    invoiceDateInfo,
    effectiveDate: relationship.effectiveDate,
    earliestBillableDate
  });
  const form = useForm({
    mode: 'all',
    defaultValues: {
      items: [createEmptyChargeItem()],
      mode: defaultMode,
      issueDate: defaultIssueDate
    }
  });
  const {
    watch
  } = form;
  const [issueDate, items] = watch(['issueDate', 'items']);
  const creditsWarning = useMemo(() => getCreditsWarning(totalCredits), [totalCredits]);
  async function onSubmit(data) {
    const {
      mode
    } = data;
    startLoader(addChargeLoader);
    const {
      success
    } = await handleRequest(sendAddChargeRequest(data));
    if (!success) {
      showErrorToast('Failed to add charge', "add-charge-".concat(mode, "-error-toast"));
    } else {
      notifyToRefresh();
      dismissModal();
      showSuccessToast(getChargeSuccessToastText(mode), "add-charge-".concat(mode, "-success-toast"));
    }
    stopLoader(addChargeLoader);
  }
  async function sendAddChargeRequest(data) {
    const {
      mode,
      issueDate,
      items
    } = data;
    const chargeItem = items[FIRST_ITEM_INDEX];
    const {
      service,
      rate,
      quantity,
      discount
    } = chargeItem.charge;
    const note = chargeItem.note;
    if (!service) {
      throw new Error('Service is required');
    }
    const total = getTotal(quantity, rate, service, discount);
    const biPayload = {
      title: service.label,
      note,
      price: total === null || total === void 0 ? void 0 : total.toDP(2).toNumber()
    };
    const submitChargesData = {
      requestedBillingTime: toDatestampObject(issueDate),
      charges: [{
        name: service.label,
        serviceTemplateId: service.value === '' ? undefined : service.value,
        note,
        rate: utils.dollarsToCents(+rate),
        quantity: utils.dollarsToCents(+quantity),
        discount: discountToDTO(discount)
      }],
      servicesToBill: []
    };
    switch (mode) {
      case BillMode.Upcoming:
        {
          logBI(10571, biPayload);
          await api.relationship.submitCharges(relationship.id, submitChargesData);
          return;
        }
      case BillMode.Now:
        {
          logBI(10579, biPayload);
          await api.relationship.billCharges(relationship.id, submitChargesData);
          return;
        }
      case BillMode.FutureDate:
        {
          logBI(10583, biPayload);
          await api.relationship.submitCharges(relationship.id, submitChargesData);
          return;
        }
    }
  }
  return /*#__PURE__*/React.createElement(FormProvider, form, /*#__PURE__*/React.createElement(ManualBillModal, {
    title: "Add Charge",
    invoiceDateInfo: invoiceDateInfo,
    effectiveDate: relationship.effectiveDate,
    onCancel: () => dismissModal(),
    billModeOptions: billModeOptions,
    onSubmit: form.handleSubmit(onSubmit),
    earliestBillableDate: earliestBillableDate,
    infoBoxContent: /*#__PURE__*/React.createElement("div", {
      className: styles.callouts
    }, !BILL_CLIENT && /*#__PURE__*/React.createElement(BillChargeClientApprovalCallout, {
      issueDate: issueDate,
      invoiceDateInfo: invoiceDateInfo
    }), creditsWarning && /*#__PURE__*/React.createElement(Callout, {
      "data-testid": "has-credits-callout",
      withBorder: true
    }, creditsWarning.text)),
    tableContent: /*#__PURE__*/React.createElement(BillChargeTableItem, {
      invoiceDateInfo: invoiceDateInfo,
      itemIndex: FIRST_ITEM_INDEX,
      chargeItem: items[FIRST_ITEM_INDEX]
    })
  }));
}